import React, { useEffect, useState } from 'react';
import { Modal, View, TouchableOpacity, Image, ScrollView } from 'react-native';
import RadioButton from '../RadioButton/RadioButton';
import closeIcon from 'src/assets/close.png';
import Button from '../Button/Button';
import Typography from '../Text/Text';
import { styles } from './RadioModal.styles';
import { formatLabel } from './helpers';

interface Option {
  label: string;
}

interface RadioModalProps {
  isVisible: boolean;
  onClose: () => void;
  title: string;
  options: Option[];
  onSelectOption: (value: string) => void;
  onButtonClickLeft: () => void;
  selectedValue?: string;
  testID?: string;
  splitLabels?: boolean;
}

const RadioModal: React.FC<RadioModalProps> = ({
  isVisible,
  onClose,
  title,
  options,
  onSelectOption,
  onButtonClickLeft,
  selectedValue = '',
  testID,
  splitLabels = false,
}) => {
  const [selectedOption, setSelectedOption] = useState<string>(selectedValue);

  const handleSaveOption = (value: string) => {
    onSelectOption(value);
    onClose();
  };

  const handleSelectOption = (value: string) => {
    setSelectedOption(value);
  };

  const renderLabel = (label: string) => {
    const { primary, secondary } = formatLabel(label, splitLabels);
    return (
      <>
        <Typography variant="title1">{primary}</Typography>
        {secondary && <Typography variant="body2">{secondary}</Typography>}
      </>
    );
  };

  useEffect(() => {
    setSelectedOption(selectedValue);
  }, [selectedValue]);

  return (
    <Modal visible={isVisible} transparent animationType="slide" testID={testID}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContainer}>
          {/* Modal header */}
          <View style={styles.header}>
            <Typography variant="h2">{title}</Typography>
            <TouchableOpacity onPress={onClose} style={styles.closeIcon}>
              {/* Use the imported image for the close icon */}
              <Image
                source={closeIcon}
                style={styles.closeImage}
                accessibilityLabel={testID + 'close-icon'}
              />
            </TouchableOpacity>
          </View>

          {/* Radio buttons */}
          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.radioButtons}>
            {options.map((option) => (
              <TouchableOpacity
                onPress={() => handleSelectOption(option.label)}
                key={option.label}
                style={styles.radioButtonContainer}>
                <View style={styles.labelContainer}>{renderLabel(option.label)}</View>
                <RadioButton
                  testID={`${testID}-${option.label}`}
                  selected={selectedOption === option.label ? true : false}
                  onPress={() => handleSelectOption(option.label)}
                />
              </TouchableOpacity>
            ))}
          </ScrollView>

          {/* Footer buttons */}
          <View style={styles.buttonContainer}>
            <View style={styles.buttonWrapper1}>
              <Button
                testID={`${testID}-back-btn`}
                type="tertiary"
                title="Cancel"
                onPress={() => onButtonClickLeft()}
              />
            </View>
            <View style={styles.buttonWrapper2}>
              <Button
                testID={`${testID}-save-btn`}
                type="primary"
                title="Confirm"
                onPress={() => handleSaveOption(selectedOption)}
              />
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default RadioModal;
